import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Bell, BellRing, BellOff, Check, Loader2, Package, Tags, Sparkles, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import api, { absoluteAsset } from '../services/api';
import { useToast } from '../hooks/use-toast';
import {
  isPushSupported,
  getPermission,
  getCurrentSubscription,
  subscribeToPush,
  unsubscribeFromPush,
} from '../services/push';

const LAST_SEEN_KEY = 'tz_notifications_last_seen';

function timeAgo(iso) {
  if (!iso) return '';
  const diff = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (diff < 60) return 'ahora';
  if (diff < 3600) return `hace ${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `hace ${Math.floor(diff / 3600)} h`;
  if (diff < 604800) return `hace ${Math.floor(diff / 86400)} d`;
  return new Date(iso).toLocaleDateString('es-ES', { day: '2-digit', month: 'short' });
}

const typeMeta = {
  product: { Icon: Package, color: 'bg-blue-50 text-blue-700' },
  brand: { Icon: Tags, color: 'bg-emerald-50 text-emerald-700' },
  post: { Icon: Sparkles, color: 'bg-amber-50 text-amber-700' },
};

const NotificationsCenter = ({ variant = 'desktop', onNavigate }) => {
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pushState, setPushState] = useState('loading'); // loading|unsupported|denied|off|on
  const [pushBusy, setPushBusy] = useState(false);
  const [unread, setUnread] = useState(0);
  const lastSeenRef = useRef(parseInt(localStorage.getItem(LAST_SEEN_KEY) || '0', 10));
  const panelRef = useRef(null);
  const btnRef = useRef(null);
  const { toast } = useToast();

  const refreshPushState = useCallback(async () => {
    if (!isPushSupported()) { setPushState('unsupported'); return; }
    const perm = getPermission();
    if (perm === 'denied') { setPushState('denied'); return; }
    try {
      const sub = await getCurrentSubscription();
      setPushState(sub ? 'on' : 'off');
    } catch {
      setPushState('off');
    }
  }, []);

  const fetchNotifications = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/notifications?limit=30');
      const list = data || [];
      setNotifications(list);
      const seen = lastSeenRef.current;
      setUnread(list.filter(n => new Date(n.createdAt).getTime() > seen).length);
    } catch {
      /* silent */
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial state + periodic refresh
  useEffect(() => {
    refreshPushState();
    fetchNotifications();
    const t = setInterval(fetchNotifications, 60000);
    return () => clearInterval(t);
  }, [refreshPushState, fetchNotifications]);

  // Mark everything as seen when opening the panel (rising edge only)
  useEffect(() => {
    if (!open) return;
    const now = Date.now();
    localStorage.setItem(LAST_SEEN_KEY, String(now));
    lastSeenRef.current = now;
    setUnread(0);
    fetchNotifications();
  }, [open, fetchNotifications]);

  // Click outside / Esc to close (desktop)
  useEffect(() => {
    if (!open || variant !== 'desktop') return undefined;
    const onClick = (e) => {
      if (panelRef.current?.contains(e.target)) return;
      if (btnRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, variant]);

  const togglePush = async () => {
    if (pushBusy) return;
    if (pushState === 'unsupported') {
      toast({ title: 'No soportado', description: 'Tu navegador no admite notificaciones push.' });
      return;
    }
    if (pushState === 'denied') {
      toast({
        title: 'Permiso bloqueado',
        description: 'Habilítalas desde la configuración del navegador para este sitio.',
      });
      return;
    }
    setPushBusy(true);
    try {
      if (pushState === 'on') {
        await unsubscribeFromPush();
        setPushState('off');
        toast({ title: 'Avisos desactivados' });
      } else {
        await subscribeToPush();
        setPushState('on');
        toast({ title: '¡Listo!', description: 'Recibirás avisos de novedades.' });
      }
    } catch (e) {
      toast({ title: 'No se pudieron activar', description: e?.message || 'Intenta de nuevo' });
      await refreshPushState();
    } finally {
      setPushBusy(false);
    }
  };

  const BellIcon = pushState === 'on' ? BellRing : (pushState === 'unsupported' || pushState === 'denied') ? BellOff : Bell;

  const handleItemClick = () => {
    setOpen(false);
    if (onNavigate) onNavigate();
  };

  const panel = (
    <div
      ref={panelRef}
      className={variant === 'desktop'
        ? 'absolute right-0 top-full z-[70] mt-2 w-[22rem] max-w-[calc(100vw-2rem)] overflow-hidden rounded-xl border border-slate-200 bg-white shadow-xl ring-1 ring-black/5'
        : 'fixed inset-0 z-[70] flex items-end bg-black/40 sm:items-center sm:justify-center'
      }
      onClick={(e) => variant === 'drawer' && e.target === e.currentTarget && setOpen(false)}
      data-testid="notifications-panel"
    >
      <div className={variant === 'drawer'
        ? 'w-full rounded-t-2xl bg-white shadow-2xl sm:max-w-md sm:rounded-2xl'
        : ''}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
          <h3 className="text-sm font-semibold text-slate-900">Notificaciones</h3>
          {variant === 'drawer' && (
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="rounded-md p-1 text-slate-400 hover:bg-slate-100"
              aria-label="Cerrar"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        {/* Push toggle row */}
        <button
          type="button"
          onClick={togglePush}
          disabled={pushBusy || pushState === 'loading'}
          className={`flex w-full items-center gap-3 border-b border-slate-100 px-4 py-3 text-left transition-colors ${
            pushState === 'on' ? 'bg-emerald-50/40 hover:bg-emerald-50' : 'bg-white hover:bg-slate-50'
          }`}
          data-testid="notifications-toggle-push"
        >
          <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${
            pushState === 'on' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'
          }`}>
            {pushBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <BellIcon className="h-4 w-4" />}
          </span>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium text-slate-900">
              {pushState === 'on' ? 'Avisos activos en este dispositivo' :
                pushState === 'denied' ? 'Permiso bloqueado por el navegador' :
                  pushState === 'unsupported' ? 'Tu navegador no admite avisos' :
                    'Recibir avisos de novedades'}
            </p>
            <p className="truncate text-xs text-slate-500">
              {pushState === 'on' ? 'Toca para desactivar' : 'Te avisamos cuando llegue algo nuevo'}
            </p>
          </div>
          {pushState === 'on' && <Check className="h-4 w-4 text-emerald-600" />}
        </button>

        {/* List */}
        <div className="max-h-[60vh] overflow-y-auto overscroll-contain">
          {loading && notifications.length === 0 ? (
            <div className="flex items-center justify-center py-10 text-slate-400">
              <Loader2 className="h-5 w-5 animate-spin" />
            </div>
          ) : notifications.length === 0 ? (
            <div className="px-6 py-10 text-center text-sm text-slate-500">
              <Bell className="mx-auto mb-2 h-6 w-6 text-slate-300" />
              Aún no hay novedades.<br />Cuando se agregue algo al catálogo aparecerá aquí.
            </div>
          ) : (
            <ul className="divide-y divide-slate-100" data-testid="notifications-list">
              {notifications.map((n) => {
                const meta = typeMeta[n.type] || typeMeta.product;
                const Icon = meta.Icon;
                const isUnread = new Date(n.createdAt).getTime() > lastSeenRef.current;
                return (
                  <li key={n.id}>
                    <Link
                      to={n.url || '/'}
                      onClick={handleItemClick}
                      className="flex gap-3 px-4 py-3 transition-colors hover:bg-slate-50"
                      data-testid={`notification-item-${n.id}`}
                    >
                      <div className={`flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-lg ${meta.color}`}>
                        {n.icon ? (
                          <img
                            src={absoluteAsset(n.icon)}
                            alt=""
                            className="h-full w-full object-contain"
                            onError={(e) => { e.currentTarget.style.display = 'none'; }}
                          />
                        ) : (
                          <Icon className="h-5 w-5" />
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium text-slate-900">{n.title}</p>
                        {n.body && <p className="truncate text-sm text-slate-600">{n.body}</p>}
                        <p className="mt-0.5 text-xs text-slate-400">{timeAgo(n.createdAt)}</p>
                      </div>
                      {isUnread && <span className="mt-2 h-2 w-2 shrink-0 rounded-full bg-emerald-500" />}
                    </Link>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  );

  // ---- Desktop trigger button ----
  if (variant === 'desktop') {
    return (
      <div className="relative">
        <button
          ref={btnRef}
          type="button"
          onClick={() => setOpen(v => !v)}
          className={`relative inline-flex h-10 w-10 items-center justify-center rounded-full border transition-colors ${
            open ? 'border-emerald-300 bg-emerald-50 text-emerald-700'
              : 'border-slate-200 bg-white text-slate-600 hover:border-emerald-300 hover:bg-emerald-50 hover:text-emerald-700'
          }`}
          aria-label="Ver notificaciones"
          aria-expanded={open}
          data-testid="notification-bell"
        >
          <BellIcon className="h-5 w-5" />
          {unread > 0 && (
            <span
              className="absolute -right-0.5 -top-0.5 flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-semibold text-white ring-2 ring-white"
              data-testid="notification-unread-badge"
            >
              {unread > 9 ? '9+' : unread}
            </span>
          )}
        </button>
        {open && panel}
      </div>
    );
  }

  // ---- Drawer trigger row + bottom sheet ----
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="flex w-full items-center gap-3 rounded-lg px-4 py-3 text-left text-base font-medium text-slate-700 transition-colors hover:bg-slate-50"
        data-testid="notification-bell-drawer"
      >
        <BellIcon className={`h-5 w-5 shrink-0 ${pushState === 'on' ? 'text-emerald-700' : 'text-slate-500'}`} />
        <span className="flex-1">Notificaciones</span>
        {unread > 0 && (
          <span className="flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-red-500 px-1.5 text-[10px] font-semibold text-white">
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>
      {open && panel}
    </>
  );
};

export default NotificationsCenter;
